import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { CommonService } from '../../common.service';
import { environment } from 'src/environments/environment';
import { customItemInterface, demo_address, document_, document_add_selection_request, document_item_response, document_procces_response, document_selectable_units_response, document_special_order_tracker_api_response, Service } from '../documents.interfaces';
import { Cliente } from 'src/app/mods/sales/sales.interfaces';
import { articulo, articulo_documento } from 'src/app/mods/inventory/inventario.interface';

@Injectable({
  providedIn: 'root'
})
export class DocumentsApiService{

  constructor(
    private http: HttpClient,
    private commonService: CommonService
  ) { }

  getBancos(): Observable<{ bancos: { id: number; nombre: string }[] }> {
    return this.http.get<{ bancos: { id: number; nombre: string }[] }>(`${environment.endpoint}documentos/pagos/bancos`)
      .pipe(catchError(this.commonService.handleRequestError));
  }

  // ============= DOCUMENTO BASE =============

  /**
   * Obtener un documento completo por ID
   */
  getDocument(id: number):Observable<document_>{
    const params = new HttpParams().set("id", id)
    return this.http.get<document_>(`${environment.endpoint}documentos/get_documento`, {params}).pipe(
      catchError(this.commonService.handleRequestError)
    )
  }

  getSpecialOrderTracker(invoiceId: number): Observable<document_special_order_tracker_api_response> {
    const params = new HttpParams().set('invoice_id', invoiceId.toString());
    return this.http
      .get<document_special_order_tracker_api_response>(`${environment.endpoint}documentos/items/special-order-tracker`, { params })
      .pipe(catchError(this.commonService.handleRequestError));
  }

  /**
   * Crear un borrador de documento vacío
   * Retorna el documento completo inicializado
   */
  createDraft(data: {
    doc_type: string;
    store: string;
    cliente: Cliente;
  }): Observable<document_> {
    const now = new Date().toISOString();
    const payload = { ...data, created: now, modified: now };
    return this.http
      .post<document_>(`${environment.endpoint}documentos/draft`, {document: payload})
      .pipe(catchError(this.commonService.handleRequestError));
  }

  /**
   * Alias: crear borrador de documento (para compatibilidad)
   */
  createDocumentDraft(data: { doc_type: string; store: string, cliente: Cliente, created?: string }): Observable<document_> {
    const now = new Date().toISOString();
    data.created = now;
    (data as any).modified = now;
    return this.createDraft(data);
  }

  /**
   * Guardar/Finalizar un documento
   * Retorna el documento completo actualizado
   */
  saveDraft(document: document_): Observable<document_> {
    const now = new Date().toISOString();
    return this.http
      .post<document_>(`${environment.endpoint}documentos/save`, { ...document, modified: now, now })
      .pipe(catchError(this.commonService.handleRequestError));
  }

  /**
   * Eliminar un borrador de documento
   * Retorna confirmación
   */
  deleteDraft(id: number): Observable<any> {
    const now = new Date().toISOString();
    return this.http
      .delete<any>(`${environment.endpoint}documentos/draft/${id}`, { body: { document_id: id, now, modified: now } })
      .pipe(catchError(this.commonService.handleRequestError));
  }

  // ============= HEADER DEL DOCUMENTO =============

  /**
   * Actualizar encabezado del documento (cliente, dirección, etc)
   * Retorna el documento completo
   */
  updateDocumentHeader(
    documentId: number,
    headerData: any
  ): Observable<document_> {
    const now = new Date().toISOString();
    return this.http
      .patch<document_>(
        `${environment.endpoint}documentos/${documentId}/header`,
        { ...headerData, document_id: documentId, modified: now, now }
      )
      .pipe(catchError(this.commonService.handleRequestError));
  }

  /**
   * Actualizar descuento global del documento
   * Retorna los nuevos totales
   */
  updateGlobalDiscount(id: number, global_discount_amount: number): Observable<document_> {
    const now = new Date().toISOString();
    return this.http.post<document_>(
      `${environment.endpoint}documentos/update_global_discount`,
      { id, global_discount_amount, modified: now }
    ).pipe(catchError(this.commonService.handleRequestError));
  }

  /**
   * Actualizar dirección de envío
   */
  updateShippingAddress(
    address: any,
    documentId: number
  ): Observable<document_> {
    const now = new Date().toISOString();
    return this.http
      .post<document_>(
        `${environment.endpoint}documentos/updateShippingAddress`,
        { document_id: documentId, address, modified: now }
      )
      .pipe(catchError(this.commonService.handleRequestError));
  }

  /**
   * Cambiar el cliente de un documento procesado.
   * Requiere admin password (validado en backend) y razon (>= 5 chars).
   * El backend migra saldo_a_favor al nuevo cliente y resetea la direccion a "Entrega en tienda".
   */
  updateDocumentCustomer(
    documentId: number,
    clienteId: number,
    authorizationId: number,
    reason: string,
  ): Observable<document_> {
    const now = new Date().toISOString();
    return this.http
      .post<document_>(
        `${environment.endpoint}documentos/updateCustomer`,
        {
          document_id: documentId,
          cliente_id: clienteId,
          authorization_id: authorizationId,
          reason,
          modified: now,
        },
      )
      .pipe(catchError(this.commonService.handleRequestError));
  }

  returnPrestamo(documentId: number): Observable<document_procces_response> {
    const now = new Date().toISOString();
    return this.http
      .post<document_procces_response>(
        `${environment.endpoint}documentos/returnPrestamo`,
        { document_id: documentId, now }
      )
      .pipe(catchError(this.commonService.handleRequestError));
  }

  // ============= ITEMS DEL DOCUMENTO =============
  // TODOS LOS item_id son el id del item en el documento
  getSelectableUnits(documentId: number, itemId: number, quantityMode = true): Observable<document_selectable_units_response> {
    const params = new HttpParams().set('document_id', documentId.toString()).set('quantity_mode', String(quantityMode));
    return this.http
      .get<document_selectable_units_response>(
        `${environment.endpoint}documentos/items/selectable-units/${itemId}`,
        { params },
      )
      .pipe(catchError(this.commonService.handleRequestError));
  }

  addItemSelection(payload: document_add_selection_request): Observable<document_item_response> {
    return this.http
      .post<document_item_response>(
        `${environment.endpoint}documentos/items/add-selection`,
        { ...payload, now: new Date().toISOString() },
      )
      .pipe(catchError(error => payload.hardware ? throwError(() => error) : this.commonService.handleRequestError(error)));
  }

    /**
   * Alias: actualizar cantidad (orden de parámetros invertida para compatibilidad)
   */
  updateQuantityItem(
    item: articulo_documento,
    document_id: number): Observable<document_item_response> {
    const now = new Date().toISOString();
    return this.http
      .post<document_item_response>(
        `${environment.endpoint}documentos/items/updateQuantity`,
        { document_id, item, now, modified: now }
      )
      .pipe(catchError(this.commonService.handleRequestError));
  }

  /**
   * Actualizar precio de un item
   * Retorna el documento completo actualizado
   */
  updatePriceItem(
    document_id: number,
    item: articulo_documento
  ): Observable<document_item_response> {
    const now = new Date().toISOString();
    return this.http
      .post<document_item_response>(
        `${environment.endpoint}documentos/items/updatePriceItem`,
        { document_id, item, now, modified: now }
      )
      .pipe(catchError(this.commonService.handleRequestError));
  }

  reorderItems(document_id: number, ordered_item_ids: number[]): Observable<document_item_response> {
    const now = new Date().toISOString();
    return this.http
      .post<document_item_response>(
        `${environment.endpoint}documentos/items/reorder`,
        { document_id, ordered_item_ids, now, modified: now },
      )
      .pipe(catchError(this.commonService.handleRequestError));
  }

  /**
   * Actualizar precios de multiples items en una sola solicitud
   * Retorna el documento completo actualizado
   */
  updatePriceItems(
    document_id: number,
    items: articulo_documento[]
  ): Observable<document_item_response> {
    const now = new Date().toISOString();
    return this.http
      .post<document_item_response>(
        `${environment.endpoint}documentos/items/updatePricesItemsInDocument`,
        { document_id, items, now, modified: now }
      )
      .pipe(catchError(this.commonService.handleRequestError));
  }

  /**
   * Actualizar nota de un item
   * Retorna el documento completo actualizado
   */
  updateItemNote(
    document_id: number,
    item: articulo_documento
  ): Observable<document_item_response> {
    const now = new Date().toISOString();
    return this.http
      .post<document_item_response>(
        `${environment.endpoint}documentos/items/updateItemNote`,
        { document_id, item, now, modified: now }
      )
      .pipe(catchError(this.commonService.handleRequestError));
  }

  /**
   * Actualizar fecha promesa de un item especial
   * Retorna el documento completo actualizado
   */
  updatePromiseDateItem(
    document_id: number,
    item: articulo_documento,
    reason?: string
  ): Observable<document_item_response> {
    const now = new Date().toISOString();
    return this.http
      .post<document_item_response>(
        `${environment.endpoint}documentos/items/updatePromiseDate`,
        { document_id, item, reason, now, modified: now }
      )
      .pipe(catchError(this.commonService.handleRequestError));
  }

  /**
   * Actualizar impuesto de un item
   * Retorna el documento completo actualizado
   */
  updateTaxItem(
    document_id: number,
    item: articulo_documento
  ): Observable<document_item_response> {
    const now = new Date().toISOString();
    return this.http
      .post<document_item_response>(
        `${environment.endpoint}documentos/items/updateTax`,
        { document_id, item, now, modified: now }
      )
      .pipe(catchError(this.commonService.handleRequestError));
  }

  /**
   * Actualizar descuento de un item
   * Retorna el documento completo actualizado
   */
  updateDiscountItem(
    document_id: number,
    item: articulo_documento
  ): Observable<document_item_response> {
    const now = new Date().toISOString();
    return this.http
      .post<document_item_response>(
        `${environment.endpoint}documentos/items/updateDiscountItem`,
        { document_id, item, now, modified: now }
      )
      .pipe(catchError(this.commonService.handleRequestError));
  }

  /**
   * Marcar item como cortesía
   * Retorna el documento completo actualizado
   */
  markAsCourtesyItem(
    document_id: number,
    item: articulo_documento,
    reason?: string
  ): Observable<document_item_response> {
    const now = new Date().toISOString();
    return this.http
      .post<document_item_response>(
        `${environment.endpoint}documentos/items/markAsCourtesy`,
        { document_id, item, reason, now, modified: now }
      )
      .pipe(catchError(this.commonService.handleRequestError));
  }

  /**
   * Desmarcar item como cortesía
   * Retorna el documento completo actualizado
   */
  unMarkAsCourtesyItem(
    document_id: number,
    item: articulo_documento,
    reason?: string
  ): Observable<document_item_response> {
    const now = new Date().toISOString();
    return this.http
      .post<document_item_response>(
        `${environment.endpoint}documentos/items/unMarkAsCourtesy`,
        { document_id, item, reason, now, modified: now }
      )
      .pipe(catchError(this.commonService.handleRequestError));
  }

  /**
   * Marcar item como pedido especial
   * Retorna el documento completo actualizado
   */
  markAsSpecialOrderItem(
    document_id: number,
    item: articulo_documento
  ): Observable<document_item_response> {
    const now = new Date().toISOString();
    return this.http
      .post<document_item_response>(
        `${environment.endpoint}documentos/items/markAsSpecialOrder`,
        { document_id, item, now, modified: now }
      )
      .pipe(catchError(this.commonService.handleRequestError));
  }

  /**
   * Marcar item como pedido especial (versión para invoice, con autorización)
   * Retorna el documento completo actualizado
   * Se mantiene separado porque en un invoice se debe regresar el STOCK a su ubicación original antes de marcar como pedido especial, lo que requiere lógica adicional y podría afectar performance si se hace para todos los documentos
   */
  markAsSpecialOrderItemInvoice(
    document_id: number,
    item: articulo_documento,
    authorization_id: number,
    reason: string
  ): Observable<document_item_response> {
    const now = new Date().toISOString();
    return this.http
      .post<document_item_response>(
        `${environment.endpoint}documentos/items/toEspecialOrder`,
        { document_id, item , authorization_id, reason, now, modified: now }
      )
      .pipe(catchError(this.commonService.handleRequestError));
  }

  /**
   * Eliminar item del documento
   * Retorna el documento completo actualizado
   */
  deleteDocumentItem(
    document_id: number,
    items: articulo_documento | articulo_documento[],
    authorization_id?: number,
    reason?: string
  ): Observable<document_item_response> {
    const now = new Date().toISOString();
    return this.http
      .post<document_item_response>(
        `${environment.endpoint}documentos/items/delete`,
        { document_id, items, authorization_id, reason, now, modified: now }
      )
      .pipe(catchError(this.commonService.handleRequestError));
  }

  /**
   * Cancelar item del documento
   * Retorna items y totales actualizados
   */
  cancelDocumentItem(
    document_id: number,
    item: articulo_documento,
    reason?: string
  ): Observable<document_item_response> {
    const now = new Date().toISOString();
    return this.http
      .post<document_item_response>(
        `${environment.endpoint}documentos/items/cancelItem`,
        { document_id, item, reason, now, modified: now }
      )
      .pipe(catchError(this.commonService.handleRequestError));
  }

  /**
   * Limpiar todos los items del documento
   * Retorna el documento completo actualizado
   */
  clearAllItems(documentId: number): Observable<document_> {
    const now = new Date().toISOString();
    return this.http
      .delete<document_>(
        `${environment.endpoint}documentos/${documentId}/items/clear`,
        { body: { document_id: documentId, now, modified: now } }
      )
      .pipe(catchError(this.commonService.handleRequestError));
  }

  // ============= MÉTODOS AUXILIARES PARA ITEMS =============

  /**
   * Agregar item como pedido especial
   */
  addItemAsSpecialOrder(
    documentId: number,
    item: articulo & { quantity?: number },
    promise_date: string,
    doc_type: string,
    store: string,
    isEco: boolean = false
  ): Observable<document_item_response> {
    const now = new Date().toISOString();  
    return this.http
      .post<document_item_response>(
        `${environment.endpoint}documentos/items/add_item_as_pe`,
        { item, document_id: documentId, date: now, promise_date, doc_type, now, store, isEco }
      );
  }


  /**
   * Agregar item personalizado
   */
  addCustomItem(
    doc_type: string,
    documentId: number,
    customItem: customItemInterface
  ): Observable<document_> {

    const now = new Date().toISOString();

    return this.http.post<document_>(
      `${environment.endpoint}documentos/items/add_custom_item`,
      { doc_type, document_id: documentId, item: customItem, now }
    ).pipe(catchError(this.commonService.handleRequestError));
  }

  /**
   * Agregar servicio al documento
   */
  addServiceDocument(
    doc_type: string,
    documentId: number,
    service: Service
  ): Observable<document_> {

    const now = new Date().toISOString();

    return this.http.post<document_>(
      `${environment.endpoint}documentos/items/add_service`,
      { document_id: documentId, service, doc_type, now }
    ).pipe(catchError(this.commonService.handleRequestError));
  }

  /**
   * Agregar DEMO (condo modelo) al documento
   */

  addDemoToDocument(
    documentId: number,
    selection: {
      document_type: string,
      demo: demo_address
    }
  ): Observable<document_> {
    const now = new Date().toISOString();
    return this.http.post<document_>(
      `${environment.endpoint}documentos/items/add_demo_to_document`,
      {
        document_id: documentId,
        demo: selection.demo,
        now,
        modified: now,
      }
    ).pipe(catchError(this.commonService.handleRequestError));
  }

  addUnlistedItem(
    document_id: number,
    new_item_id: number,
    old_item_id: number
  ): Observable<document_> {
    const now = new Date().toISOString();
    return this.http
      .post<document_>(
        `${environment.endpoint}documentos/add_unlisted_item`,
        { document_id, new_item_id, old_item_id, now, modified: now }
      )
      .pipe(catchError(this.commonService.handleRequestError));
  }

  // ============= NOTAS Y TÉRMINOS =============

  /**
   * Actualizar nota pública del documento
   * Retorna el documento completo actualizado
   */
  updatePublicNote(
    documentId: number,
    publicNote: string
  ): Observable<document_> {
    const now = new Date().toISOString();
    return this.http
      .post<document_>(
        `${environment.endpoint}documentos/public_note`,
        { public_note: publicNote, document_id: documentId, modified: now, now }
      )
      .pipe(catchError(this.commonService.handleRequestError));
  }

  /**
   * Obtener términos de pago
   */
  getPaymentTerms(documentId: number): Observable<string> {
    return this.http
      .get<string>(
        `${environment.endpoint}documentos/${documentId}/payment-terms`
      )
      .pipe(catchError(this.commonService.handleRequestError));
  }

  // ============= TIPO DE CAMBIO =============

  /**
   * Obtener tipo de cambio para una fecha
   */
  getExchangeRate(date: string): Observable<number> {
    return this.http
      .get<number>(
        `${environment.endpoint}documentos/exchange-rate/${date}`
      )
      .pipe(catchError(this.commonService.handleRequestError));
  }

  // ============= ACCIONES DE DOCUMENTO =============

  /**
   * Procesar invoice (cambiar estado y generar transacción)
   * Retorna el documento completo actualizado
   */
  processInvoice(documentId: number): Observable<document_procces_response> {
    const now = new Date().toISOString();
    return this.http
      .post<document_procces_response>(
        `${environment.endpoint}documentos/processInvoice`,
        { document_id: documentId , now}
      )
      .pipe(catchError(this.commonService.handleRequestError));
  }

  /**
   * Convertir estimado a invoice
   * Retorna el documento completo actualizado con nuevo tipo
   */
  convertEstimateToInvoice(document_: document_, now: string): Observable<document_procces_response> {
    const localNow = new Date().toISOString();
    return this.http
      .post<document_procces_response>(
        `${environment.endpoint}documentos/convert-to-invoice`,
        {document_, now: localNow || now}
      )
      .pipe(catchError(this.commonService.handleRequestError));
  }

  /**
   * Verificar si existe documento previo para este cliente
   */
  hasPreviousDocument(data: any): Observable<any> {
    return this.http
      .post<any>(
        `${environment.endpoint}documentos/check-previous`,
        data
      )
      .pipe(catchError(this.commonService.handleRequestError));
  }

  // ============= BÚSQUEDAS =============

  /**
   * Buscar items por término
   */
  searchItems(
    searchTerm: string,
    store?: string,
    limit: number = 10,
    mode: 'normal' | 'exact' = 'normal',
    exactType?: 'id' | 'sku' | 'qr'
  ): Observable<articulo[]> {
    const params = {
      search: searchTerm,
      store: store || '',
      limit: limit.toString(),
      mode,
      ...(exactType ? { exactType } : {}),
    };
    return this.http
      .get<articulo[]>(
        `${environment.endpoint}documentos/items/searchItems`,
        { params: params as any }
      )
      .pipe(catchError(this.commonService.handleRequestError));
  }

  /**
   * Obtener servicios disponibles por tienda
   */
  get_services_by_store(store: string): Observable<any[]> {
    return this.http
      .get<Service[]>(
        `${environment.endpoint}documentos/services/${store}`
      )
      .pipe(catchError(this.commonService.handleRequestError));
  }

  /**
   * Obtener proveedores disponibles por tienda
   */
  get_suppliers_by_store(store: string): Observable<any[]> {
    return this.http
      .get<any[]>(
        `${environment.endpoint}documentos/suppliers/${store}`
      )
      .pipe(catchError(this.commonService.handleRequestError));
  }

  // ============= Shares Sale/Equivalente =============

  authorizeAction(password: string, reason: string, action: string): Observable<any> {
    return this.http.post<any>(`${environment.endpoint}auth/authorize`, { password, reason, action }).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  // ============= SWAP entre invoices procesadas =============

  /**
   * Obtener candidatos de donante para un swap entre pedidos especiales
   * de invoices procesadas. Se respeta el filtrado por store y se excluye
   * la propia invoice/fila.
   */
  getSwapCandidates(id_item_in_document: number, qty: number = 1): Observable<any> {
    const params = new HttpParams()
      .set('id_item_in_document', String(id_item_in_document))
      .set('qty', String(qty || 1));
    // No aplicamos handleRequestError para preservar err.error.code en UI.
    return this.http.get<any>(
      `${environment.endpoint}documentos/items/swap/candidates`,
      { params },
    );
  }

  /**
   * Ejecutar el swap. El backend valida autorizacion y ventana de 21d.
   * No aplica handleRequestError para conservar codigos de error (p.ej.
   * DONOR_PROMISE_TOO_CLOSE, DONOR_QTY_INSUFFICIENT, etc.) en la UI.
   */
  executeSwap(payload: {
    donor_source: 'allocation' | 'balance';
    donor_id: number;
    donor_id_item_in_document: number;
    donor_kind: string;
    requester_id_item_in_document: number;
    requester_kind: string;
    qty: number;
    authorization_id: number;
    notes?: string;
    document_id?: number;
  }): Observable<any> {
    return this.http.post<any>(
      `${environment.endpoint}documentos/items/swap/execute`,
      { ...payload, now: new Date().toISOString() },
    );
  }

  getSalesExecutives(): Observable<any[]> {
    return this.http.get<any[]>(`${environment.endpoint}users/sales_executives`).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  shareSale(document_id: number, item: any, reason?: string): Observable<document_item_response> {
    const now = new Date().toISOString();
    return this.http.post<document_item_response>(`${environment.endpoint}documentos/items/share_sale`, { document_id, item: { ...item, now }, reason, now, modified: now }).pipe(
      catchError(this.commonService.handleRequestError)
    );
  }

  reassignSaleUsers(
    document_id: number,
    id_item_in_document: number,
    id_user_added: number,
    id_user_shared_sale: number | null,
    authorization_id: number,
    reason: string,
  ): Observable<document_item_response> {
    const now = new Date().toISOString();
    return this.http.post<document_item_response>(`${environment.endpoint}documentos/items/reassign_sale`, {
      document_id,
      id_item_in_document,
      id_user_added,
      id_user_shared_sale,
      authorization_id,
      reason,
      now,
      modified: now,
    }).pipe(catchError(this.commonService.handleRequestError));
  }

  /** Materializa un pedido especial con una unidad QR exacta por cada fila. */
  assignUnitsToSpecialOrderItem(
    id_item_in_document: number,
    document_id: number,
    item: articulo_documento,
    unit_ids: number[],
    authorization_id?: number,
    reason?: string
  ): Observable<document_item_response> {
    const now = new Date().toISOString();
    return this.http
      .post<document_item_response>(
        `${environment.endpoint}documentos/items/toInStock`,
        { id_item_in_document, document_id, item, unit_ids, authorization_id, reason, now, modified: now }
      )
      .pipe(catchError(this.commonService.handleRequestError));
  }

  // ============= CONTRACT =============

  /**
   * Marcar una invoice como Contract (requiere admin password)
   */
  markAsContract(documentId: number, authorizationId: number, reason: string): Observable<any> {
    const now = new Date().toISOString();
    return this.http
      .post<any>(`${environment.endpoint}documentos/mark-as-contract`, {
        document_id: documentId,
        authorization_id: authorizationId,
        reason,
        now,
        modified: now,
      })
      .pipe(catchError(this.commonService.handleRequestError));
  }

  /** Marcar toda la invoice como venta no comisionable, con autorización y motivo. */
  markAsNonCommissionable(documentId: number, authorizationId: number, reason: string): Observable<any> {
    const now = new Date().toISOString();
    return this.http.post<any>(`${environment.endpoint}documentos/mark-as-non-commissionable`, {
      document_id: documentId, authorization_id: authorizationId, reason, now, modified: now,
    }).pipe(catchError(this.commonService.handleRequestError));
  }

  /*============DEMO============*/
  processDemo(document_: document_): Observable<document_procces_response> {
    const now = new Date().toISOString();
    return this.http
      .post<document_procces_response>(
        `${environment.endpoint}documentos/processDemo`,
        { document_: document_, now }
      )
      .pipe(catchError(this.commonService.handleRequestError));
  }

  convertToDemo(documentId: number): Observable<document_procces_response> {
    const now = new Date().toISOString();
    return this.http
      .post<document_procces_response>(
        `${environment.endpoint}documentos/convertToDemo`,
        { document_id: documentId , now}
      )
      .pipe(catchError(this.commonService.handleRequestError));
  }

  processPrestamo(documentId: number): Observable<document_procces_response> {
    const now = new Date().toISOString();
    return this.http
      .post<document_procces_response>(
        `${environment.endpoint}documentos/processPrestamo`,
        { document_id: documentId, now }
      )
      .pipe(catchError(this.commonService.handleRequestError));
  }

  getDemosAvailables(store: string): Observable<demo_address[]> {
    const params = new HttpParams().set('store', store);
    return this.http
      .get<demo_address[]>(`${environment.endpoint}documentos/getDemosAvailables`, { params })
      .pipe(catchError(this.commonService.handleRequestError));
  }

  // ============= PAGOS =============

  registerPayments(document_id: number, payments: any[], exchange_rate: number): Observable<any> {
    const now = new Date().toISOString();
    return this.http
      .post<any>(`${environment.endpoint}documentos/pagos/register`, { document_id, payments, exchange_rate, now, modified: now })
      .pipe(catchError(this.commonService.handleRequestError));
  }

  getPaymentHistory(document_id: number): Observable<any> {
    return this.http
      .get<any>(`${environment.endpoint}documentos/pagos/history/${document_id}`)
      .pipe(catchError(this.commonService.handleRequestError));
  }

  getDocumentBalance(document_id: number): Observable<any> {
    return this.http
      .get<any>(`${environment.endpoint}documentos/pagos/balance/${document_id}`)
      .pipe(catchError(this.commonService.handleRequestError));
  }

  getClientBalance(client_id: number): Observable<any> {
    return this.http
      .get<any>(`${environment.endpoint}documentos/pagos/saldo-cliente/${client_id}`)
      .pipe(catchError(this.commonService.handleRequestError));
  }

  getClientSaldoHistory(client_id: number, store?: string, limit: number = 100, offset: number = 0): Observable<any> {
    let params = new HttpParams().set('limit', limit.toString()).set('offset', offset.toString());
    if (store) params = params.set('store', store);
    return this.http
      .get<any>(`${environment.endpoint}documentos/pagos/saldo-cliente/${client_id}/historial`, { params })
      .pipe(catchError(this.commonService.handleRequestError));
  }

  // === Reportes financieros ===

  getReportInvoicesBalance(filters: { store?: string; date_from?: string; date_to?: string; payment_status?: string; page?: number; page_size?: number }): Observable<any> {
    let params = new HttpParams();
    if (filters.store) params = params.set('store', filters.store);
    if (filters.date_from) params = params.set('date_from', filters.date_from);
    if (filters.date_to) params = params.set('date_to', filters.date_to);
    if (filters.payment_status) params = params.set('payment_status', filters.payment_status);
    if (filters.page) params = params.set('page', String(filters.page));
    if (filters.page_size) params = params.set('page_size', String(filters.page_size));
    return this.http
      .get<any>(`${environment.endpoint}documentos/pagos/reportes/balance-invoices`, { params })
      .pipe(catchError(this.commonService.handleRequestError));
  }

  getReportCollection(filters: { store?: string; date_from?: string; date_to?: string; confirmation_status?: string; payment_method?: string; voided?: string; page?: number; page_size?: number }): Observable<any> {
    let params = new HttpParams();
    if (filters.store) params = params.set('store', filters.store);
    if (filters.date_from) params = params.set('date_from', filters.date_from);
    if (filters.date_to) params = params.set('date_to', filters.date_to);
    if (filters.confirmation_status) params = params.set('confirmation_status', filters.confirmation_status);
    if (filters.payment_method) params = params.set('payment_method', filters.payment_method);
    if (filters.voided) params = params.set('voided', filters.voided);
    if (filters.page) params = params.set('page', String(filters.page));
    if (filters.page_size) params = params.set('page_size', String(filters.page_size));
    return this.http
      .get<any>(`${environment.endpoint}documentos/pagos/reportes/cobranza`, { params })
      .pipe(catchError(this.commonService.handleRequestError));
  }

  getReportAccountsReceivable(filters: { store?: string; page?: number; page_size?: number }): Observable<any> {
    let params = new HttpParams();
    if (filters.store) params = params.set('store', filters.store);
    if (filters.page) params = params.set('page', String(filters.page));
    if (filters.page_size) params = params.set('page_size', String(filters.page_size));
    return this.http
      .get<any>(`${environment.endpoint}documentos/pagos/reportes/cuentas-por-cobrar`, { params })
      .pipe(catchError(this.commonService.handleRequestError));
  }

  getReportClientBalances(filters: { page?: number; page_size?: number }): Observable<any> {
    let params = new HttpParams();
    if (filters.page) params = params.set('page', String(filters.page));
    if (filters.page_size) params = params.set('page_size', String(filters.page_size));
    return this.http
      .get<any>(`${environment.endpoint}documentos/pagos/reportes/saldos-clientes`, { params })
      .pipe(catchError(this.commonService.handleRequestError));
  }

  getReportClientChargesDetail(client_id: number): Observable<any> {
    return this.http
      .get<any>(`${environment.endpoint}documentos/pagos/reportes/saldos-clientes/${client_id}/cargos`)
      .pipe(catchError(this.commonService.handleRequestError));
  }

  getReportClientSaldoDetail(client_id: number): Observable<any> {
    return this.http
      .get<any>(`${environment.endpoint}documentos/pagos/reportes/saldos-clientes/${client_id}/detalle`)
      .pipe(catchError(this.commonService.handleRequestError));
  }

  getReportPaymentsByMethod(filters: { store?: string; date_from?: string; date_to?: string; method_search?: string; page?: number; page_size?: number }): Observable<any> {
    let params = new HttpParams();
    if (filters.store) params = params.set('store', filters.store);
    if (filters.date_from) params = params.set('date_from', filters.date_from);
    if (filters.date_to) params = params.set('date_to', filters.date_to);
    if (filters.method_search?.trim()) params = params.set('method_search', filters.method_search.trim());
    if (filters.page) params = params.set('page', String(filters.page));
    if (filters.page_size) params = params.set('page_size', String(filters.page_size));
    return this.http
      .get<any>(`${environment.endpoint}documentos/pagos/reportes/por-metodo-pago`, { params })
      .pipe(catchError(this.commonService.handleRequestError));
  }

  confirmPaymentTransaction(transactionId: number, authorization_id: number, reason: string): Observable<any> {
    return this.http
      .put<any>(`${environment.endpoint}documentos/pagos/confirm/${transactionId}`, { authorization_id, reason })
      .pipe(catchError(this.commonService.handleRequestError));
  }

  voidPaymentTransaction(transactionId: number, authorization_id: number, reason: string): Observable<any> {
    return this.http
      .put<any>(`${environment.endpoint}documentos/pagos/void-transaction/${transactionId}`, { authorization_id, reason })
      .pipe(catchError(this.commonService.handleRequestError));
  }

  classifySaldo(transactionId: number, commissionable_amount: number, authorization_id: number, reason: string, expected_amount: number, expected_commissionable: number): Observable<any> {
    return this.http.put<any>(`${environment.endpoint}documentos/pagos/saldo-manual/commission-split/${transactionId}`, { commissionable_amount, authorization_id, reason, expected_amount, expected_commissionable }).pipe(catchError(this.commonService.handleRequestError));
  }

  // ============= SALDOS MANUALES (ADMINISTRACIÓN) =============

  addManualSaldoDeposit(client_id: number, amount: number, reference: string, authorization_id: number, reason: string, commissionable_amount?: number | null): Observable<any> {
    return this.http
      .post<any>(`${environment.endpoint}documentos/pagos/saldo-manual/add`, { client_id, amount, reference, authorization_id, reason, commissionable_amount })
      .pipe(catchError(this.commonService.handleRequestError));
  }

  cancelSaldoTransaction(transaction_id: number, authorization_id: number, reason: string): Observable<any> {
    return this.http
      .put<any>(`${environment.endpoint}documentos/pagos/saldo-manual/cancel/${transaction_id}`, { authorization_id, reason })
      .pipe(catchError(this.commonService.handleRequestError));
  }

  adjustClientBalance(client_id: number, adjustment_amount: number, reference: string, authorization_id: number, reason: string): Observable<any> {
    return this.http
      .put<any>(`${environment.endpoint}documentos/pagos/saldo-manual/adjust`, { client_id, adjustment_amount, reference, authorization_id, reason })
      .pipe(catchError(this.commonService.handleRequestError));
  }

  // ============= CANCELACIONES =============

  /**
   * Cancelar un item individual de una invoice confirmada.
   * Requiere autorización de admin.
   */
  cancelInvoiceItem(payload: {
    document_id: number;
    item: any;
    refund_type: 'none' | 'cash_refund' | 'saldo_a_favor';
    penalty_percent?: number;
    commission_paid?: boolean;
    commissionable_amount?: number | null;
    quantity?: number;
    authorization_id: number;
    reason: string;
  }): Observable<document_item_response> {
    const now = new Date().toISOString();
    // No usamos handleRequestError aquí porque necesitamos preservar
    // el body con `code` (p.ej. PENDING_PAYMENTS) para mostrar UX dedicada.
    return this.http
      .post<document_item_response>(
        `${environment.endpoint}documentos/cancellations/cancel-item`,
        { ...payload, now, modified: now }
      );
  }

  returnDemo(documentId: number): Observable<any> {
    return this.http
      .post<any>(`${environment.endpoint}documentos/returnDemo`, { document_id: documentId })
      .pipe(catchError(this.commonService.handleRequestError));
  }

  /** Cancela exactamente las filas físicas seleccionadas del documento. */
  cancelDocumentLines(payload: {
    document_id: number;
    document_line_ids: number[];
    authorization_id: number;
    reason: string;
    refund_type?: 'none' | 'cash_refund' | 'saldo_a_favor';
    penalty_percent?: number;
    commission_paid?: boolean;
    commissionable_amount?: number | null;
  }): Observable<any> {
    const now = new Date().toISOString();
    return this.http.post<any>(
      `${environment.endpoint}documentos/cancellations/cancel-item`,
      {
        ...payload,
        now,
        modified: now,
        refund_type: payload.refund_type ?? 'none',
        penalty_percent: payload.penalty_percent ?? 0,
      },
    );
  }

  /**
   * Cancelar una invoice completa.
   * Requiere autorización de admin.
   */
  cancelFullInvoice(payload: {
    document_id: number;
    refund_type: 'none' | 'cash_refund' | 'saldo_a_favor';
    penalty_percent?: number;
    commission_paid?: boolean;
    commissionable_amount?: number | null;
    authorization_id: number;
    reason: string;
  }): Observable<any> {
    const now = new Date().toISOString();
    // No usamos handleRequestError: necesitamos preservar el body con `code`.
    return this.http
      .post<any>(
        `${environment.endpoint}documentos/cancellations/cancel-invoice`,
        { ...payload, now, modified: now }
      );
  }

  /**
   * Calcula el contexto financiero antes de cancelar (no muta nada).
   * Devuelve { confirmed_paid, new_invoice_total_after_cancel, excess,
   *            pending_payments_count, pending_payments[], target_item_total }.
   */
  previewCancellation(document_id: number, item_id?: number | null, quantity?: number | null, lineIds?: number[]): Observable<any> {
    let params = new HttpParams().set('document_id', String(document_id));
    if (item_id) params = params.set('item_id', String(item_id));
    if (quantity) params = params.set('quantity', String(quantity));
    if (lineIds?.length) params = params.set('document_line_ids', JSON.stringify(lineIds));
    return this.http
      .get<any>(`${environment.endpoint}documentos/cancellations/preview`, { params })
      .pipe(catchError(this.commonService.handleRequestError));
  }

  /**
   * Resuelve el saldo de un item cancelado por proveedor. Internamente el
   * backend invoca cancelItem con penalty_percent=0 forzado y luego marca
   * supplier_cancellation_resolved_at. Reusa autorizacion admin estandar.
   */
  resolveSupplierBalance(payload: {
    document_id: number;
    id_item_in_document: number;
    commission_paid?: boolean;
    commissionable_amount?: number | null;
    refund_type: 'none' | 'cash_refund' | 'saldo_a_favor';
    authorization_id: number;
    reason: string;
  }): Observable<document_item_response> {
    return this.http
      .post<document_item_response>(
        `${environment.endpoint}documentos/cancellations/cancel-item`,
        {
          document_id: payload.document_id,
          item: { id_item_in_document: payload.id_item_in_document },
          refund_type: payload.refund_type,
          penalty_percent: 0,
          commission_paid: payload.commission_paid,
          commissionable_amount: payload.commissionable_amount,
          authorization_id: payload.authorization_id,
          reason: payload.reason,
        },
      );
  }

  // ============= REPORTES DE CANCELACIONES / REFUNDS / TIMELINE =============

  getReportCancellations(filters: {
    store?: string; date_from?: string; date_to?: string;
    cancellation_type?: string; refund_type?: string;
    page?: number; page_size?: number;
  }): Observable<any> {
    let params = new HttpParams();
    if (filters.store) params = params.set('store', filters.store);
    if (filters.date_from) params = params.set('date_from', filters.date_from);
    if (filters.date_to) params = params.set('date_to', filters.date_to);
    if (filters.cancellation_type) params = params.set('cancellation_type', filters.cancellation_type);
    if (filters.refund_type) params = params.set('refund_type', filters.refund_type);
    if (filters.page) params = params.set('page', String(filters.page));
    if (filters.page_size) params = params.set('page_size', String(filters.page_size));
    return this.http
      .get<any>(`${environment.endpoint}documentos/pagos/reportes/cancelaciones`, { params })
      .pipe(catchError(this.commonService.handleRequestError));
  }

  getReportRefunds(filters: {
    store?: string; date_from?: string; date_to?: string;
    page?: number; page_size?: number;
  }): Observable<any> {
    let params = new HttpParams();
    if (filters.store) params = params.set('store', filters.store);
    if (filters.date_from) params = params.set('date_from', filters.date_from);
    if (filters.date_to) params = params.set('date_to', filters.date_to);
    if (filters.page) params = params.set('page', String(filters.page));
    if (filters.page_size) params = params.set('page_size', String(filters.page_size));
    return this.http
      .get<any>(`${environment.endpoint}documentos/pagos/reportes/refunds`, { params })
      .pipe(catchError(this.commonService.handleRequestError));
  }

  getReportConfirmationTimeline(filters: {
    store?: string; date_from?: string; date_to?: string;
    status?: string; page?: number; page_size?: number;
  }): Observable<any> {
    let params = new HttpParams();
    if (filters.store) params = params.set('store', filters.store);
    if (filters.date_from) params = params.set('date_from', filters.date_from);
    if (filters.date_to) params = params.set('date_to', filters.date_to);
    if (filters.status) params = params.set('status', filters.status);
    if (filters.page) params = params.set('page', String(filters.page));
    if (filters.page_size) params = params.set('page_size', String(filters.page_size));
    return this.http
      .get<any>(`${environment.endpoint}documentos/pagos/reportes/confirmation-timeline`, { params })
      .pipe(catchError(this.commonService.handleRequestError));
  }

  // ============= SPECIAL ORDER BREAKDOWN =============

  /**
   * Obtener desglose granular de un item especial.
   * Muestra fragmentación de cajas, estados de transición, y trazabilidad.
   */
  getItemBreakdown(id_item_in_document: number, total_ordered: number = 0): Observable<any> {
    const params = new HttpParams().set('total_ordered', String(total_ordered));
    return this.http
      .get<any>(`${environment.endpoint}documentos/item/${id_item_in_document}/breakdown`, { params })
      .pipe(catchError(this.commonService.handleRequestError));
  }

}
